import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, Clock, MapPin, ChevronRight } from "lucide-react";

function evtClass(t) {
  return ({
    "Drug Administration": "bg-sky-50 text-sky-700 border-sky-200",
    "Clinic Visit": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Lab Draw": "bg-amber-50 text-amber-700 border-amber-200",
    "Radiology": "bg-purple-50 text-purple-700 border-purple-200",
    "Custom": "bg-stone-50 text-stone-700 border-stone-200",
  }[t] || "bg-stone-50 text-stone-700 border-stone-200");
}

export default function MobileCalendar() {
  const [tab, setTab] = useState("upcoming");
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/me/calendar").then((r) => setData(r.data)); }, []);

  const events = useMemo(() => {
    if (!data?.calendars) return [];
    const today = new Date().toISOString().slice(0, 10);
    const all = [];
    data.calendars.forEach((c) => {
      c.events.forEach((e) => all.push({ ...e, regimen_name: c.regimen_name }));
    });
    if (tab === "upcoming") return all.filter((e) => e.date >= today).sort((a,b) => a.date.localeCompare(b.date));
    return all.filter((e) => e.date < today).sort((a,b) => b.date.localeCompare(a.date));
  }, [data, tab]);

  return (
    <div className="max-w-md mx-auto" data-testid="mobile-calendar-page">
      <header className="px-5 pt-6 pb-4">
        <h1 className="font-display text-2xl font-bold text-stone-900">My schedule</h1>
        <p className="text-stone-500 text-sm">All your appointments in one place.</p>
      </header>

      <div className="px-5 mb-4">
        <div className="bg-stone-100 rounded-xl p-1 flex">
          <button onClick={() => setTab("upcoming")} className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${tab === "upcoming" ? "bg-white text-stone-900 shadow-soft" : "text-stone-500"}`} data-testid="tab-upcoming">Upcoming</button>
          <button onClick={() => setTab("past")} className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${tab === "past" ? "bg-white text-stone-900 shadow-soft" : "text-stone-500"}`} data-testid="tab-past">Past</button>
        </div>
      </div>

      <div className="px-5 space-y-2.5">
        {events.length === 0 && <div className="bg-white border border-stone-200 rounded-xl p-6 text-center text-stone-500 text-sm">Nothing here yet.</div>}
        {events.map((e) => (
          <button key={e.id} onClick={() => nav(`/m/event/${e.id}`)} className="w-full text-left bg-white border border-stone-200 rounded-xl p-4 hover:shadow-soft" data-testid={`event-${e.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-stone-50 flex flex-col items-center justify-center shrink-0">
                <span className="text-[10px] uppercase font-semibold text-stone-500">{new Date(e.date).toLocaleDateString(undefined, { month: "short" })}</span>
                <span className="text-lg font-display font-bold text-stone-900">{new Date(e.date).getDate()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-stone-900 truncate">{e.name}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${evtClass(e.event_type)}`}>{e.event_type}</span>
                  {e.time && <span className="text-xs text-stone-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{e.time}</span>}
                </div>
                {e.location && <div className="text-xs text-stone-500 inline-flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{e.location}</div>}
              </div>
              <ChevronRight className="w-4 h-4 text-stone-400" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
